import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  QUERY_SETTINGS,
  getSettings,
  saveSettings
} from "api/landlord/landlord";
import { useAuthContext } from "providers/Auth";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";
import { SyncOption } from "./integrations/pms/constants";
import { PropertyManagementSystem } from "shared/types/partner";

export const useSettingsController = () => {
  const { partner } = useAuthContext();
  const queryClient = useQueryClient();
  const { data: settingsData } = useQuery({
    queryKey: [QUERY_SETTINGS],
    queryFn: () => getSettings()
  });

  const { mutateAsync: updateSettings, isPending: isUpdateSettingsPending } =
    useMutation({
      mutationFn: saveSettings,
      onSuccess: () => {
        toast.success("Integration details have been updated successfully!");
        queryClient.invalidateQueries({ queryKey: [QUERY_SETTINGS] });
      },
      onError: (err) => {
        toast.error(
          "Error saving integration details: " + getErrorMessage(err)
        );
      }
    });

  const getSettingName = (name: string) =>
    settingsData?.settings?.find((setting) => setting.name === name)?.value;

  const propertyManagementSystem = partner?.property_management_system?.name;
  const hubspotHubId = partner?.hubspot_hub_id;
  // Parse Google Analytics ID with backwards compatibility
  const parseGoogleAnalyticsId = (
    value: string | null | undefined
    // eslint-disable-next-line sonarjs/function-return-type -- Intentional mixed return for backwards compatibility
  ): string | string[] | undefined => {
    if (!value) return undefined;
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : value;
    } catch {
      return value;
    }
  };

  const isAppfolioEverySyncEnabled =
    propertyManagementSystem === PropertyManagementSystem.Appfolio &&
    (getSettingName("appfolio_sync") || SyncOption.EVERY_SYNC) ===
      SyncOption.EVERY_SYNC;

  const isRentvineEverySyncEnabled =
    propertyManagementSystem === PropertyManagementSystem.Rentvine &&
    (getSettingName("rentvine_sync") || SyncOption.NEVER) ===
      SyncOption.EVERY_SYNC;

  const isPropertywareEverySyncEnabled =
    propertyManagementSystem === PropertyManagementSystem.Propertyware &&
    (getSettingName("propertyware_sync") || SyncOption.EVERY_SYNC) ===
      SyncOption.EVERY_SYNC;

  const isRentManagerEverySyncEnabled =
    propertyManagementSystem === PropertyManagementSystem.RentManager &&
    (getSettingName("rent_manager_sync") || SyncOption.EVERY_SYNC) ===
      SyncOption.EVERY_SYNC;

  const isRentersWarehouse =
    propertyManagementSystem === PropertyManagementSystem.RentersWarehouse;

  const isManageAmerica =
    propertyManagementSystem === PropertyManagementSystem.ManageAmerica;

  const isYardi = propertyManagementSystem === PropertyManagementSystem.Yardi;

  const isEverySyncEnabled =
    isAppfolioEverySyncEnabled ||
    isRentvineEverySyncEnabled ||
    isPropertywareEverySyncEnabled ||
    isRentManagerEverySyncEnabled ||
    isRentersWarehouse ||
    isManageAmerica ||
    isYardi;

  return {
    settingsData,
    currentSettings: {
      google: {
        googleAnalyticsId: parseGoogleAnalyticsId(
          getSettingName("google_analytics_id")
        )
      },
      resman: {
        apiKey: getSettingName("resman_api_key"),
        accountId: getSettingName("resman_account_id"),
        partnerId: getSettingName("resman_partner_id")
      },
      buildium: {
        clientId: getSettingName("buildium_client_id"),
        clientSecret: getSettingName("buildium_client_secret"),
        email: getSettingName("buildium_email"),
        password: getSettingName("buildium_password"),
        subdomain: getSettingName("buildium_subdomain")
      },
      rentManager: {
        companyCode: getSettingName("rent_manager_company_code"),
        rentManagerSync: getSettingName("rent_manager_sync")
      },
      rentersWarehouse: {
        rentersWarehouseApiKey: getSettingName("renters_warehouse_api_key"),
        rentersWarehouseUrl: getSettingName("renters_warehouse_url"),
        rentersWarehouseSalesforceDomain: getSettingName(
          "renters_warehouse_salesforce_domain"
        ),
        rentersWarehouseSalesforceClientId: getSettingName(
          "renters_warehouse_salesforce_client_id"
        ),
        rentersWarehouseSalesforceClientSecret: getSettingName(
          "renters_warehouse_salesforce_client_secret"
        ),
        rentersWarehouseSalesforceUsername: getSettingName(
          "renters_warehouse_salesforce_username"
        ),
        rentersWarehouseSalesforcePassword: getSettingName(
          "renters_warehouse_salesforce_password"
        )
      },
      manageAmerica: {
        manageAmericaCompanyId: getSettingName("manage_america_company_id")
      },
      rentvine: {
        rentvineAccount: getSettingName("rentvine_account"),
        rentvineApiKey: getSettingName("rentvine_api_key"),
        rentvineApiSecret: getSettingName("rentvine_api_secret"),
        rentvineSync: getSettingName("rentvine_sync")
      },
      quext: {
        quextCustomerId: getSettingName("quext_customer_id")
      },
      yardi: {
        webServiceUrl: getSettingName("yardi_web_service_url"),
        username: getSettingName("yardi_username"),
        password: getSettingName("yardi_password"),
        serverName: getSettingName("yardi_server_name"),
        database: getSettingName("yardi_database"),
        enableApplicationCharge: getSettingName(
          "yardi_enable_application_charge"
        ),
        applicationChargeCode: getSettingName("yardi_application_charge_code"),
        enableScreeningServicesCharge: getSettingName(
          "yardi_enable_screening_services_charge"
        ),
        screeningServicesChargeCode: getSettingName(
          "yardi_screening_services_charge_code"
        ),
        generalLedgerAccount: getSettingName("yardi_general_ledger_account"),
        enableTemplateFeeCharges: getSettingName(
          "yardi_enable_template_fee_charges"
        ),
        administrationFeeChargeCode: getSettingName(
          "yardi_administration_fee_charge_code"
        ),
        holdingFeeChargeCode: getSettingName("yardi_holding_fee_charge_code")
      },
      appfolio: {
        appfolioUsername: getSettingName("appfolio_username"),
        appfolioPassword: getSettingName("appfolio_password"),
        appfolioUrl: getSettingName("appfolio_url"),
        appfolioSync: getSettingName("appfolio_sync"),
        appfolioUseReports: getSettingName("appfolio_use_reports"),
        appfolioUsePropertyGroups: getSettingName(
          "appfolio_use_property_groups"
        ),
        appfolioSelectedPropertyGroups: getSettingName(
          "appfolio_selected_property_groups"
        ),
        appfolioEnableRently: getSettingName("appfolio_enable_rently"),
        appfolioUseSupergoodApi: getSettingName("appfolio_use_supergood_api"),
        appfolioSupergoodUsername: getSettingName(
          "appfolio_supergood_username"
        ),
        appfolioSupergoodPassword: getSettingName(
          "appfolio_supergood_password"
        ),
        appfolioSupergoodMfaPhone: getSettingName(
          "appfolio_supergood_mfa_phone"
        )
      },
      entrata: {
        entrataDomain: getSettingName("entrata_domain"),
        entrataUsername: getSettingName("entrata_username"),
        entrataPassword: getSettingName("entrata_password"),
        entrataMfaEmail: getSettingName("entrata_mfa_email")
      },
      sdk: {
        secretKey: getSettingName("secret_key"),
        accessKey: getSettingName("access_key")
      },
      pms: {
        pms_prospect_creation_trigger: getSettingName(
          "pms_prospect_creation_trigger"
        ),
        pms_prospect_creation_on_approve: getSettingName(
          "pms_prospect_creation_on_approve"
        ),
        pms_prospect_creation_on_conditional_approve: getSettingName(
          "pms_prospect_creation_on_conditional_approve"
        ),
        pms_prospect_creation_on_decline: getSettingName(
          "pms_prospect_creation_on_decline"
        ),
        rent_manager_create_twa: getSettingName("rent_manager_create_twa"),
        pms_import_inventory: getSettingName("pms_import_inventory")
      },
      propertyware: {
        propertywareClientId: getSettingName("propertyware_client_id"),
        propertywareClientSecret: getSettingName("propertyware_client_secret"),
        propertywareSystemId: getSettingName("propertyware_system_id"),
        propertywareSync: getSettingName("propertyware_sync")
      }
    },
    isAppfolioEverySyncEnabled,
    isRentersWarehouse,
    isManageAmerica,
    hubspotHubId,
    isEverySyncEnabled,
    propertyManagementSystem,
    updateSettings,
    isUpdateSettingsPending
  };
};

export const getAvailableSettings = (
  propertyManagementSystem?: PropertyManagementSystem | ""
) => {
  const decisionSettings = [
    "pms_prospect_creation_on_approve",
    "pms_prospect_creation_on_conditional_approve",
    "pms_prospect_creation_on_decline"
  ];

  const submissionOnlySettings = ["pms_prospect_creation_trigger"];

  const allSettings = [...submissionOnlySettings, ...decisionSettings];

  switch (propertyManagementSystem) {
    case PropertyManagementSystem.RentManager:
      return [...allSettings, "rent_manager_create_twa"];
    case PropertyManagementSystem.RentersWarehouse:
    case PropertyManagementSystem.ManageAmerica:
      return submissionOnlySettings;
    case PropertyManagementSystem.Appfolio:
    case PropertyManagementSystem.Rentvine:
      return allSettings;
    case PropertyManagementSystem.Propertyware:
    case PropertyManagementSystem.Resman:
    case PropertyManagementSystem.Buildium:
    case PropertyManagementSystem.Yardi:
    case PropertyManagementSystem.Quext:
    case PropertyManagementSystem.Entrata:
    default:
      return decisionSettings;
  }
};
